/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { AppState, Auth0Provider } from "@auth0/auth0-react";
import { Box, Container, Grid } from "@material-ui/core";
import {
    AppLoadingIndicatorV1,
    PageContentsGridV1,
} from "@startree-ui/platform-ui";
import React, {
    createContext,
    FunctionComponent,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { SecurityConfiguration } from "../../rest/dto/security.interfaces";
import { getSecurityConfiguration } from "../../rest/security/security.rest";
import { AuthProviderLoader } from "../auth-provider-loader/auth-provider-loader.component";
import { AuthConfigContextProps } from "./auth-provider-auth0.interfaces";

const AuthConfigContext = createContext<AuthConfigContextProps>(
    {} as AuthConfigContextProps
);

export const useAuthConfigProvider = (): AuthConfigContextProps => {
    return useContext(AuthConfigContext);
};

export const AuthProviderAuth0: FunctionComponent = ({ children }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const [clientId, setClientId] = useState<string | null>(null);
    const [authConfigAudience, setAuthConfigAudience] = useState<string | null>(
        null
    );
    const [authConfigDomain, setAuthConfigDomain] = useState<string | null>(
        null
    );

    const config = useMemo(() => {
        return {
            domain: authConfigDomain,
            clientId: clientId,
        };
    }, [clientId, authConfigAudience, authConfigDomain]);

    const onRedirectCallback = (appState?: AppState): void => {
        history.push(appState?.returnTo || window.location.pathname);
    };

    useEffect(() => {
        getSecurityConfiguration()
            .then((authConfiguration: SecurityConfiguration | null) => {
                if (
                    !authConfiguration ||
                    !authConfiguration.clientId ||
                    !authConfiguration.audience
                ) {
                    setIsError(true);

                    return;
                }

                const domain = authConfiguration.domain;
                const audienceUrl = authConfiguration.audience;

                if (!audienceUrl || !domain) {
                    // Invalid audience path
                    setIsError(true);

                    return;
                }

                setClientId(authConfiguration.clientId);
                setAuthConfigDomain(domain);
                setAuthConfigAudience(audienceUrl);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // Loading indicator
    if (isLoading) {
        return <AppLoadingIndicatorV1 />;
    }

    // Error state
    if (isError) {
        return (
            <Container maxWidth="md">
                <PageContentsGridV1>
                    <Grid item xs={12}>
                        <Box pb={20} pt={20}>
                            {t(
                                "message.experienced-issue-while-trying-initiate-app"
                            )}
                        </Box>
                    </Grid>
                </PageContentsGridV1>
            </Container>
        );
    }

    return (
        <Auth0Provider
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: authConfigAudience as string,
            }}
            clientId={clientId as string}
            domain={authConfigDomain as string}
            onRedirectCallback={onRedirectCallback}
        >
            <AuthConfigContext.Provider value={config}>
                <AuthProviderLoader>{children}</AuthProviderLoader>
            </AuthConfigContext.Provider>
        </Auth0Provider>
    );
};
